import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axios';
import './OBSPresetsSettings.css';

// ─────────────────────────────────────────────
// TransformEditor — editiert Transform-Werte einer Cam
// ─────────────────────────────────────────────
function TransformEditor({ camKey, transform, onChange }) {
  const label = { cam1: 'Cam 1', cam2: 'Cam 2', cam3: 'Cam 3' }[camKey] || camKey;

  const handleChange = (field, value) => {
    onChange(camKey, { ...transform, [field]: field === 'visible' ? value : Number(value) });
  };

  return (
    <div className="transform-editor">
      <div className="transform-editor__label">{label}</div>
      <div className="transform-editor__fields">
        <label>
          X
          <input
            type="number"
            value={transform.positionX ?? 0}
            onChange={(e) => handleChange('positionX', e.target.value)}
          />
        </label>
        <label>
          Y
          <input
            type="number"
            value={transform.positionY ?? 0}
            onChange={(e) => handleChange('positionY', e.target.value)}
          />
        </label>
        <label>
          Breite
          <input
            type="number"
            value={transform.width ?? 0}
            onChange={(e) => handleChange('width', e.target.value)}
          />
        </label>
        <label>
          Höhe
          <input
            type="number"
            value={transform.height ?? 0}
            onChange={(e) => handleChange('height', e.target.value)}
          />
        </label>
        <label className="transform-editor__visible">
          Sichtbar
          <input
            type="checkbox"
            checked={transform.visible ?? false}
            onChange={(e) => handleChange('visible', e.target.checked)}
          />
        </label>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PresetTransformRow — eine Zeile in der Preset-Tabelle
// ─────────────────────────────────────────────
function PresetTransformRow({ preset, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const [transforms, setTransforms] = useState(
    preset.config?.transforms || { cam1: {}, cam2: {}, cam3: {} }
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleTransformChange = (camKey, newTransform) => {
    setTransforms((prev) => ({ ...prev, [camKey]: newTransform }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(preset.id, {
        config: { ...preset.config, transforms }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="preset-transform-row">
      <div
        className="preset-transform-row__header"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="preset-transform-row__name">{preset.name}</span>
        <span className="preset-transform-row__category">{preset.category || preset.type}</span>
        <span className="preset-transform-row__toggle">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="preset-transform-row__body">
          {['cam1', 'cam2', 'cam3'].map((camKey) => (
            <TransformEditor
              key={camKey}
              camKey={camKey}
              transform={transforms[camKey] || {}}
              onChange={handleTransformChange}
            />
          ))}
          <div className="preset-transform-row__actions">
            <button
              className={`btn-save ${saved ? 'btn-save--saved' : ''}`}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Speichern...' : saved ? '✓ Gespeichert' : 'Speichern'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// OBSPresetsSettings — Hauptkomponente
// ─────────────────────────────────────────────
export default function OBSPresetsSettings() {
  const [config, setConfig] = useState({
    streamSceneName: 'Stream',
    pauseSceneName: 'Pause',
    settingsSceneName: 'Einstellungen',
    camSources: { cam1: 'Cam 1', cam2: 'Cam 2', cam3: 'Cam 3' }
  });
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [configRes, presetsRes] = await Promise.all([
        axiosInstance.get('/api/obs-presets/config'),
        axiosInstance.get('/api/obs-presets')
      ]);
      setConfig(configRes.data);
      setPresets(presetsRes.data);
    } catch (err) {
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleConfigChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setConfigSaved(false);
  };

  const handleCamSourceChange = (camKey, value) => {
    setConfig((prev) => ({
      ...prev,
      camSources: { ...prev.camSources, [camKey]: value }
    }));
    setConfigSaved(false);
  };

  const handleConfigSave = async () => {
    setConfigSaving(true);
    try {
      await axiosInstance.post('/api/obs-presets/config', config);
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 2000);
    } catch (err) {
      setError('Fehler beim Speichern der Konfiguration');
    } finally {
      setConfigSaving(false);
    }
  };

  const handlePresetSave = async (id, data) => {
    await axiosInstance.put(`/api/obs-presets/${id}`, data);
    // Presets neu laden
    const res = await axiosInstance.get('/api/obs-presets');
    setPresets(res.data);
  };

  const layoutPresets = presets.filter((p) => p.type === 'layout');

  if (loading) {
    return (
      <div className="obs-presets-settings obs-presets-settings--loading">
        <div className="settings-spinner" />
        <p>Lade Einstellungen...</p>
      </div>
    );
  }

  return (
    <div className="obs-presets-settings">
      <div className="obs-presets-settings__header">
        <h1>⚙️ OBS Presets — Einstellungen</h1>
        <a href="/obs-presets" className="back-link">← Zurück zu OBS Presets</a>
      </div>

      {error && (
        <div className="settings-error">❌ {error}</div>
      )}

      {/* ── Kamera-Quellnamen ── */}
      <section className="settings-section">
        <h2 className="settings-section__title">Kamera-Quellnamen in OBS</h2>
        <p className="settings-section__desc">
          Trage hier die exakten Namen deiner Kamera-Quellen aus OBS ein.
        </p>
        <div className="settings-fields">
          {['cam1', 'cam2', 'cam3'].map((camKey) => (
            <label key={camKey} className="settings-field">
              <span>{camKey === 'cam1' ? 'Cam 1' : camKey === 'cam2' ? 'Cam 2' : 'Cam 3'}</span>
              <input
                type="text"
                value={config.camSources?.[camKey] || ''}
                onChange={(e) => handleCamSourceChange(camKey, e.target.value)}
                placeholder={`z.B. Cam ${camKey.slice(-1)}`}
              />
            </label>
          ))}
        </div>
      </section>

      {/* ── Szenen-Namen ── */}
      <section className="settings-section">
        <h2 className="settings-section__title">OBS Szenen-Namen</h2>
        <p className="settings-section__desc">
          Namen der OBS-Szenen, die das System steuert.
        </p>
        <div className="settings-fields">
          <label className="settings-field">
            <span>Stream-Szene</span>
            <input
              type="text"
              value={config.streamSceneName || ''}
              onChange={(e) => handleConfigChange('streamSceneName', e.target.value)}
              placeholder="Stream"
            />
          </label>
          <label className="settings-field">
            <span>Pause-Szene</span>
            <input
              type="text"
              value={config.pauseSceneName || ''}
              onChange={(e) => handleConfigChange('pauseSceneName', e.target.value)}
              placeholder="Pause"
            />
          </label>
          <label className="settings-field">
            <span>Einstellungs-Szene</span>
            <input
              type="text"
              value={config.settingsSceneName || ''}
              onChange={(e) => handleConfigChange('settingsSceneName', e.target.value)}
              placeholder="Einstellungen"
            />
          </label>
        </div>

        <button
          className={`btn-save ${configSaved ? 'btn-save--saved' : ''}`}
          onClick={handleConfigSave}
          disabled={configSaving}
        >
          {configSaving ? 'Speichern...' : configSaved ? '✓ Gespeichert' : 'Konfiguration speichern'}
        </button>
      </section>

      {/* ── Preset Transform-Werte ── */}
      <section className="settings-section">
        <h2 className="settings-section__title">Preset Transform-Werte</h2>
        <p className="settings-section__desc">
          Passe die Position (X/Y) und Größe (Breite/Höhe) jeder Kamera pro Preset an.
          Alle Werte in Pixeln bei 1920×1080.
        </p>
        <div className="preset-transform-list">
          {layoutPresets.map((preset) => (
            <PresetTransformRow
              key={preset.id}
              preset={preset}
              onSave={handlePresetSave}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
